import { useEffect, useState } from "react";
import { ArrowLeft, CalendarDays, MessageCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import { SITE, whatsappLink } from "@/lib/site";
import { fetchAds, type Ad } from "@/lib/content-client";
import { SiteHeader } from "@/components/SiteHeader";
import { Reveal } from "@/components/Reveal";
import { SplitText } from "@/components/SplitText";
import "./index.css";

/** Renders a YYYY-MM-DD bound as something readable, e.g. "12 Dec 2026". */
function prettyDay(value: string | null) {
  if (!value) return null;
  const parsed = new Date(`${value}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

/** "Ends 12 Dec 2026" / "From 12 Dec 2026" / "Open-ended". */
function window_label(ad: Ad) {
  const from = prettyDay(ad.startsAt);
  const to = prettyDay(ad.endsAt);
  if (from && to) return `${from} — ${to}`;
  if (from) return `From ${from}`;
  if (to) return `Until ${to}`;
  return null;
}

/** Bounds a card's image frame so one extreme ratio cannot distort the grid. */
const MIN_AD_RATIO = 0.72;
const MAX_AD_RATIO = 2.1;

function adRatioFor(ad: Ad) {
  if (!ad.width || !ad.height) return 16 / 10;
  return Math.min(Math.max(ad.width / ad.height, MIN_AD_RATIO), MAX_AD_RATIO);
}

function AdCard({ ad, index }: { ad: Ad; index: number }) {
  const window = window_label(ad);
  const adRatio = adRatioFor(ad);
  // The lead card gets the wide treatment so the newest promotion reads first.
  const feature = index === 0;

  return (
    <Reveal variant="up" delay={Math.min(index, 4) * 90}>
      <article
        className={cn(
          "group relative flex h-full flex-col overflow-hidden border border-gold/25 bg-gold/[0.04] transition-colors duration-500 hover:border-gold/50",
          feature ? "md:flex-row" : "flex-col",
        )}
      >
        {ad.src && (
          <div
            className={cn(
              "relative overflow-hidden bg-espresso/30",
              feature ? "md:w-1/2 md:shrink-0" : "w-full",
            )}
            // The box takes the image's real ratio, clamped at both ends: a
            // 9:16 phone shot gets a tall frame instead of being cropped to a
            // strip, and a 3:1 banner gets a wide one. Past the clamp the image
            // is covered rather than letterboxed, so the card keeps its shape.
            style={{ aspectRatio: String(adRatio) }}
          >
            <img
              src={ad.src}
              alt=""
              loading="lazy"
              decoding="async"
              className="h-full w-full object-cover transition-transform duration-[1.4s] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.05]"
            />
            <span
              aria-hidden="true"
              className="pointer-events-none absolute inset-0 bg-gradient-to-t from-espresso/50 to-transparent"
            />
          </div>
        )}

        <div className="flex flex-1 flex-col p-6 sm:p-8">
          <div className="flex flex-wrap items-center gap-3">
            {ad.kicker && (
              <span className="font-sans text-[0.5625rem] tracking-[0.28em] text-gold uppercase">
                {ad.kicker}
              </span>
            )}
            {window && (
              <span className="inline-flex items-center gap-1.5 rounded-full border border-ivory/15 px-2.5 py-1 font-sans text-[0.5625rem] tracking-[0.14em] text-ivory/55 uppercase">
                <CalendarDays className="h-3 w-3" strokeWidth={1.5} />
                {window}
              </span>
            )}
          </div>

          <h2
            className={cn(
              "mt-4 font-display leading-[1.1] font-light",
              feature ? "text-3xl sm:text-4xl" : "text-2xl",
            )}
          >
            {ad.headline}
          </h2>

          {ad.body && (
            <p
              className={cn(
                "mt-4 font-sans leading-relaxed font-light text-ivory/65",
                feature ? "text-sm sm:text-base" : "text-sm",
              )}
            >
              {ad.body}
            </p>
          )}

          {ad.ctaLabel && ad.ctaUrl && (
            <a
              href={ad.ctaUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-7 inline-flex w-fit items-center gap-2 rounded-full bg-gold px-7 py-3 font-sans text-[0.625rem] tracking-[0.22em] text-espresso uppercase transition-transform duration-500 hover:-translate-y-0.5"
            >
              {ad.ctaLabel}
            </a>
          )}

          {/* gold rule that draws in on hover */}
          <span
            aria-hidden="true"
            className="mt-auto block h-px w-full origin-left scale-x-0 bg-gold/60 pt-0 transition-transform duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-x-100"
          />
        </div>
      </article>
    </Reveal>
  );
}

export function AdsPage() {
  const [items, setItems] = useState<Ad[] | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let live = true;
    fetchAds().then(data => {
      if (!live) return;
      if (data.ok) setItems(data.items);
      else {
        setFailed(true);
        setItems([]);
      }
    });
    return () => {
      live = false;
    };
  }, []);

  useEffect(() => {
    document.title = `Announcements — ${SITE.name}`;
  }, []);

  const loading = items === null;

  return (
    <div className="grain min-h-screen bg-espresso text-ivory">
      <SiteHeader tone="dark" />

      <main>
        <section className="relative overflow-hidden pt-32 pb-16 sm:pt-40 sm:pb-20">
          <div aria-hidden="true" className="hatch pointer-events-none absolute inset-0 opacity-[0.05]" />
          <div
            aria-hidden="true"
            className="animate-float-soft pointer-events-none absolute -top-24 right-0 h-[30rem] w-[30rem] rounded-full bg-gold/10 blur-[120px]"
          />

          <div className="relative mx-auto max-w-6xl px-6 lg:px-10">
            <Reveal variant="fade">
              <a
                href="/"
                className="group inline-flex items-center gap-2 font-sans text-[0.625rem] tracking-[0.24em] text-ivory/50 uppercase transition-colors hover:text-gold"
              >
                <ArrowLeft
                  className="h-3.5 w-3.5 transition-transform duration-500 group-hover:-translate-x-1"
                  strokeWidth={1.5}
                />
                Back to the studio
              </a>
            </Reveal>

            <Reveal variant="fade" delay={80}>
              <p className="mt-10 font-sans text-[0.6875rem] font-medium tracking-[0.32em] text-gold uppercase">
                Announcements
              </p>
            </Reveal>

            <SplitText
              as="h1"
              text="Current and upcoming."
              className="mt-6 max-w-4xl font-display text-[clamp(2.5rem,7.5vw,5.5rem)] leading-[1.02] font-light"
              stagger={55}
            />

            <Reveal variant="up" delay={200}>
              <p className="mt-8 max-w-xl font-sans text-sm leading-relaxed font-light text-ivory/70 sm:text-base">
                Open dates, seasonal offers, and anything worth knowing before you get in
                touch. Anything past its end date retires itself.
              </p>
            </Reveal>
          </div>
        </section>

        <section className="mx-auto max-w-6xl px-6 pb-24 lg:px-10">
          {loading && (
            <div className="grid gap-6 md:grid-cols-2">
              {Array.from({ length: 2 }).map((_, i) => (
                <div key={i} className="h-64 animate-pulse rounded-sm bg-ivory/[0.06]" />
              ))}
            </div>
          )}

          {!loading && failed && (
            <p className="py-20 text-center font-sans text-sm font-light text-ivory/60">
              Announcements could not be loaded. Please try again shortly.
            </p>
          )}

          {!loading && !failed && items.length === 0 && (
            <div className="flex flex-col items-center py-20 text-center">
              <h2 className="font-display text-2xl font-light">Nothing running right now</h2>
              <p className="mt-3 max-w-sm font-sans text-sm leading-relaxed font-light text-ivory/55">
                There are no live announcements at the moment. Availability and pricing are
                always worth asking about directly.
              </p>
              <a
                href={whatsappLink()}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-8 inline-flex items-center gap-2.5 rounded-full bg-gold px-8 py-3.5 font-sans text-[0.6875rem] tracking-[0.22em] text-espresso uppercase transition-transform duration-500 hover:-translate-y-0.5"
              >
                <MessageCircle className="h-4 w-4" strokeWidth={1.5} />
                Ask about availability
              </a>
            </div>
          )}

          {!loading && items.length > 0 && (
            <div className="grid gap-6 md:grid-cols-2">
              {items.map((ad, i) => (
                <AdCard
                  key={ad.id}
                  ad={ad}
                  index={i}
                />
              ))}
            </div>
          )}
        </section>
      </main>

      <footer className="border-t border-ivory/10 px-6 py-10 lg:px-10">
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-4 sm:flex-row">
          <p className="font-display text-base tracking-[0.2em] uppercase">{SITE.name}</p>
          <p className="font-sans text-[0.625rem] tracking-[0.2em] text-ivory/40 uppercase">
            &copy; {new Date().getFullYear()} {SITE.name}. All rights reserved.
          </p>
          <a
            href="/art"
            className="font-sans text-[0.625rem] tracking-[0.2em] text-ivory/40 uppercase transition-colors hover:text-gold"
          >
            The portfolio
          </a>
        </div>
      </footer>
    </div>
  );
}

export default AdsPage;
